import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useRef, forwardRef, useImperativeHandle, } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import HighlightOff from '@mui/icons-material/HighlightOff';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Box from '../Box/Box';
const useStyles = makeStyles(createStyles({
    root: {
        '@media (min-width: 600px)': {
            paddingInline: '1.5em',
        },
        width: '100%',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
    },
    searchFieldContainer: {
        position: 'relative',
        display: 'inline',
        width: '100%',
        height: '100%',
    },
    searchField: {
        padding: 'unset',
        marginTop: 'unset',
        width: '100%',
        '& > .MuiInputLabel-root': {
            color: 'rgba(256,256,256,0.8) !important',
            fontWeight: 'normal',
            fontSize: 14,
            top: '-5px',
        },
        '& .Mui-focused': {
            color: 'white',
        },
        '& .MuiSelect-filled': {
            paddingRight: '60px !important',
        },
        '& :before': {
            borderBottom: '2px solid rgba(256,256,256,0.8) !important',
            '@media (max-width: 1024px)': {
                display: 'none',
            },
        },
        '& :after': {
            borderBottom: '2px solid white !important',
            '@media (max-width: 1024px)': {
                display: 'none',
            },
        },
    },
    searchFieldInput: {
        marginBottom: 'unset',
        '& > .MuiSelect-icon': {
            color: 'white',
        },
        '@media (max-width: 1024px)': {
            paddingBottom: '0 !important',
        },
    },
    searchFieldInputInner: {
        paddingTop: '18px !important',
        paddingBottom: '0.2em !important',
        '@media (max-width: 1024px)': {
            paddingBottom: '11px !important',
        },
        color: 'white !important',
        maxWidth: 'calc(100% - 60px)',
    },
    searchFieldLabel: {
        '&.MuiInputLabel-shrink': {
            top: '-0.3em',
            left: '-0.3em',
            color: 'rgba(256,256,256,0.5) !important',
            fontWeight: 'normal',
            fontSize: 14,
        },
    },
    inputClearContainer: {
        marginTop: 'auto',
        marginBottom: 'auto',
        position: 'absolute',
        right: '0px',
        top: '-2px',
        height: 'auto !important',
        '@media (max-width: 600px)': {
            top: '-5px',
            width: '10% !important',
        },
    },
    clearButton: {
        color: 'white',
        '& .Mui-disabled': {
            color: 'rgba(256,256,256, 0.3)',
        },
        '@media (max-width: 1024px)': {
            color: 'white !important',
        },
        top: '5px',
        right: '10px',
        '@media (min-width: 600px)': {
            top: '11.5px',
        },
        '@media (min-width: 1025px)': {
            top: '9.5px',
        },
    },
    clearIcon: {
        '@media (max-width: 600px)': {
            height: '1.6em !important',
            width: '1.6em !important',
        },
        height: '0.8em',
        width: '0.8em',
    },
    prefixSettingCheckbox: {
        '& > .MuiFormControlLabel-label': {
            font: 'bold 14px "Helvetica Neue", Helvetica, Arial, sans-serif',
        },
        '@media (max-width: 600px)': {
            '& > .MuiFormControlLabel-label': {
                font: 'bold 12px "Helvetica Neue", Helvetica, Arial, sans-serif',
            },
        },
    },
}));
const SearchField = forwardRef((props, ref) => {
    const classes = useStyles();
    const { onPrefixSettingChange, onValueChange, placeholderText, disabled, nonPrefixMatching, } = props;
    const [inputValue, setInputValue] = useState('');
    const [shrinkLabel, setShrinkLabel] = useState(false);
    const [nonPrefixMatchesAllowed, setNonPrefixMatchesAllowed] = useState(nonPrefixMatching);
    const inputField = useRef();
    const onSearchFieldValueChange = (event) => {
        const { value } = event.target;
        setInputValue(value);
        const trimmedValue = value.trim();
        if (trimmedValue.length >= 3) {
            onValueChange(trimmedValue);
        }
        else {
            onValueChange('');
        }
    };
    const handleCheckboxChange = (event) => {
        setNonPrefixMatchesAllowed(event.target.checked);
        onPrefixSettingChange(event.target.checked);
    };
    const refocusInput = (refParam = inputField) => {
        const inputRef = refParam.current;
        if (inputRef && typeof inputRef.value === 'string' && !inputRef.disabled) {
            inputRef.focus();
        }
        else {
            setTimeout(refocusInput, 100, refParam);
        }
    };
    const clearInput = () => {
        setInputValue('');
        setShrinkLabel(false);
        onValueChange('');
        refocusInput();
    };
    useImperativeHandle(ref, () => ({
        clearInputWithoutSearch() {
            setInputValue('');
            setShrinkLabel(false);
            refocusInput();
        },
        focusInput() {
            refocusInput();
        },
    }));
    return (_jsxs(Box, { className: classes.root, children: [_jsx(FormControlLabel, { className: classes.prefixSettingCheckbox, control: (_jsx(Checkbox, { checked: nonPrefixMatchesAllowed, onChange: handleCheckboxChange, disabled: disabled })), label: "Part match" }), _jsxs(Box, { className: classes.searchFieldContainer, children: [_jsx(TextField, { inputRef: inputField, InputLabelProps: {
                            shrink: shrinkLabel && !disabled,
                            variant: 'filled',
                            className: classes.searchFieldLabel,
                        }, InputProps: {
                            className: classes.searchFieldInput,
                        }, value: inputValue, inputProps: { className: classes.searchFieldInputInner }, className: classes.searchField, variant: "filled", label: disabled ? 'Search can\'t be further refined' : placeholderText || 'Search', onChange: onSearchFieldValueChange, disabled: disabled, onFocus: () => { setShrinkLabel(shrinkLabel || true); }, onBlur: () => { setShrinkLabel(inputValue !== ''); } }), _jsx(Box, { className: classes.inputClearContainer, children: inputValue === '' ? null : (_jsx(IconButton, { size: "small", "aria-label": "clear", onClick: clearInput, className: classes.clearButton, children: _jsx(HighlightOff, { className: classes.clearIcon }) })) })] })] }));
});
export default SearchField;
